import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    MapPin,
    ArrowLeft,
    PenLine,
    User as UserIcon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { updateMyProfile } from "../lib/api";

const EDIT = {
    root: "edit-profile-page",
    nameInput: "profile-name-input",
    parkInput: "profile-park-input",
    bioInput: "profile-bio-input",
    saveBtn: "profile-save-btn",
    cancelBtn: "edit-profile-cancel-btn",
};

const EditProfile = () => {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [park, setPark] = useState("");
    const [bio, setBio] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!user) return;
        setName(user.name || "");
        setPark(user.park || "");
        setBio(user.bio || "");
    }, [user]);

    if (!user) return null;

    const handleSave = async () => {
        if (!name.trim()) {
            toast.error("Name can't be empty");
            return;
        }
        try {
            setSaving(true);
            const fresh = await updateMyProfile({
                name: name.trim(),
                park: park.trim() || null,
                bio: bio.trim() || null,
            });
            setUser(fresh);
            toast.success("Profile updated");
            navigate("/profile", { replace: true });
        } catch (err) {
            toast.error("Couldn't save changes", {
                description:
                    err?.response?.data?.detail || "Try again in a moment.",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <main
            data-testid={EDIT.root}
            className="relative z-10 mx-auto max-w-2xl px-6 py-14 md:px-10 md:py-20"
        >
            <Link
                to="/profile"
                data-testid={EDIT.cancelBtn}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#737a3d] hover:underline"
            >
                <ArrowLeft className="h-3 w-3" strokeWidth={2.5} />
                Back to profile
            </Link>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-[#2e3220] sm:text-5xl">
                Edit profile
            </h1>
            <p className="mt-2 text-base text-[#5a6142]">
                Update the name, park, and short bio your neighbors see when they
                browse your listings.
            </p>

            <section className="mt-8 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-6">
                <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                    About you
                </p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Display name
                        </label>
                        <input
                            data-testid={EDIT.nameInput}
                            value={name}
                            onChange={(e) => setName(e.target.value.slice(0, 120))}
                            className="mt-1 w-full rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-2.5 text-sm text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Park / neighborhood
                        </label>
                        <div className="mt-1 flex items-center gap-2 rounded-2xl border border-[#2e3220]/15 bg-white/70 px-3">
                            <MapPin className="h-3.5 w-3.5 text-[#737a3d]" />
                            <input
                                data-testid={EDIT.parkInput}
                                value={park}
                                onChange={(e) => setPark(e.target.value.slice(0, 120))}
                                placeholder="e.g. Easton Park"
                                className="w-full bg-transparent py-2.5 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:outline-none"
                            />
                        </div>
                    </div>
                </div>
                <div className="mt-4">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                        Short bio
                    </label>
                    <textarea
                        data-testid={EDIT.bioInput}
                        value={bio}
                        onChange={(e) => setBio(e.target.value.slice(0, 600))}
                        rows={3}
                        placeholder="One or two sentences so neighbors know who they're borrowing from."
                        className="mt-1 w-full resize-none rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-2.5 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                    />
                    <p className="mt-1 text-right text-[10px] text-[#5a6142]/70">
                        {bio.length}/600
                    </p>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                    <Link to={`/neighbors/${user.user_id}`} className="btn-ghost">
                        <UserIcon className="h-4 w-4" strokeWidth={2.5} />
                        View public page
                    </Link>
                    <button
                        type="button"
                        data-testid={EDIT.saveBtn}
                        onClick={handleSave}
                        disabled={saving}
                        className="btn-primary"
                    >
                        {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <PenLine className="h-4 w-4" strokeWidth={2.5} />
                        )}
                        Save changes
                    </button>
                </div>
            </section>
        </main>
    );
};

export default EditProfile;
